import { Permission } from "@edgetier/watchtower-types";
import type { IPermissionDetails } from "./permission-details.types";

/**
 * The name and description shown for each permission. The name replaces the backend's, so the wording can change
 * without a backend release. Entries are ordered by permission group.
 */
export const PermissionDetails: Record<Permission, IPermissionDetails> = {
    // General.
    [Permission.ViewExplore]: {
        label: "View Explore",
        description: "Access the Explore screen.",
    },
    [Permission.ViewCustomReports]: {
        label: "View Custom Reports",
        description: "Access the Custom Reports screen.",
    },
    [Permission.ViewCustomerInsights]: {
        label: "View customer insights",
        description:
            "Access the Customers screen, including aggregated insights, AI customer summaries, and customer emotions.",
    },
    [Permission.ViewLive]: {
        label: "View live",
        description: "View real-time agents, queues, and interactions.",
    },

    // Agents & Activity.
    [Permission.ViewAgent]: {
        label: "View Agents & Activity",
        description: "Analyse agents' performance on the Agents & Activity screens.",
    },
    [Permission.ViewAgentDetails]: {
        label: "View agent details",
        description: "Agent names and email addresses are displayed rather than anonymised.",
    },
    [Permission.ViewOwnAgentActivity]: {
        label: "View own agent activity",
        description: "Agents can log in and view only their own activity.",
    },
    [Permission.ManageAgentTags]: {
        label: "Manage agent groups",
        description: "Assign or remove agents from groups.",
    },

    // Users & Roles.
    [Permission.ManageUsers]: {
        label: "Manage users",
        description: "Invite, edit, and remove EdgeTier users.",
    },
    [Permission.ManageRoles]: {
        label: "Manage roles",
        description: "View, create and update role permissions.",
    },
    [Permission.ManageTeams]: {
        label: "Manage teams",
        description: "Create, update, or remove user teams.",
    },

    // Reviews & Scorecards.
    [Permission.ManageAllReviews]: {
        label: "Manage all reviews",
        description: "Create, edit, and remove all users' reviews and scorecards.",
    },
    [Permission.ViewAllReviews]: {
        label: "View all reviews",
        description: "View all reviews without ability to create, edit and remove them.",
    },
    [Permission.ManageOwnReviews]: {
        label: "Manage own reviews",
        description: "Create, edit, and remove your own reviews, without access to those of other users.",
    },
    [Permission.ViewReceivedReviews]: {
        label: "View received reviews",
        description: "Agents see reviews sent to them, without the ability to edit or delete them.",
    },
    [Permission.ManageScorecards]: {
        label: "Manage scorecards",
        description: "Create, edit and remove scorecards used as templates for reviews.",
    },

    // Tags.
    [Permission.ManageInteractionTags]: {
        label: "Manage interaction tags",
        description: "Create, edit and remove tags from interactions.",
    },
    [Permission.ManagePhraseTriggers]: {
        label: "Manage phrase triggers",
        description: "Create, edit and remove rules that apply tags to messages matching specific phrases.",
    },
    [Permission.TagPastInteractions]: {
        label: "Tag past interactions",
        description: "Apply phrase tags to past interactions retroactively.",
    },
    // Product has not supplied new copy for the "Edit contact reasons" label yet, so the original description stands.
    [Permission.ManageAiTags]: {
        label: "Edit contact reasons",
        description: "Create and update AI tag definitions and prompts.",
    },

    // Interactions.
    [Permission.AddOrRemoveEmotions]: {
        label: "Manage emotions",
        description: "Add or remove emotions from messages.",
    },
    [Permission.ManageWritingIssues]: {
        label: "Manage writing issues",
        description: "Create, edit and remove spelling exemptions.",
    },
    [Permission.PlayAllCallRecordings]: {
        label: "Play all call recordings",
        description: "Listen to call recordings for any agent.",
    },
    [Permission.PlayOwnCallRecordings]: {
        label: "Play own call recordings",
        description: "Listen only to recordings of your own calls.",
    },
    [Permission.DownloadInteractions]: {
        label: "Export CSVs",
        description: "Download interactions as a CSV file.",
    },
    [Permission.ExportInteractionData]: {
        label: "Export PDFs and transcripts",
        description: "Copy interaction transcripts and download interactions as a PDF.",
    },

    // Sonar.
    [Permission.ViewAnomalies]: {
        label: "View anomalies",
        description: "View previous anomalies detected by the models.",
    },
    [Permission.ManageAnomalyModels]: {
        label: "Manage anomaly models",
        description: "Create and edit your organisation's anomaly models.",
    },

    // Ask Spotlight.
    [Permission.UseAskSpotlight]: {
        label: "Use Ask Spotlight",
        description: "Access the Ask Spotlight feature.",
    },
    [Permission.ManageAiRules]: {
        label: "Manage Ask Spotlight context",
        description: "Manage the company context Ask Spotlight answers from.",
    },
    [Permission.ViewAskSpotlightUsage]: {
        label: "View Ask Spotlight usage",
        description: "View Ask Spotlight usage analytics.",
    },
    [Permission.ShareAskSpotlightTemplates]: {
        label: "Share Ask Spotlight templates",
        description: "Share your Ask Spotlight templates with your organisation.",
    },
    [Permission.SendAskSpotlightReports]: {
        label: "Send Ask Spotlight reports",
        description: "Send Ask Spotlight reports to other users, now or on a schedule.",
    },

    // Integrations & API.
    [Permission.UseMcp]: {
        label: "Use MCP",
        description: "Access EdgeTier data through the MCP server.",
    },
};
